import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RefreshCw } from 'lucide-react'
import { useArtefactTemplates } from '../../../hooks'
import { VelociraptorTemplateStep } from '../../../components/collections/VelociraptorTemplateStep'
import type { CollectionRequest, UpdateCollectionRequestInput } from '../../../types/case-container'

const TEXTAREA_ROWS = 3

interface EditCollectionModalProps {
  caseId: string
  request: CollectionRequest
  onClose: () => void
  onUpdate: (input: UpdateCollectionRequestInput) => void
  updating: boolean
  error: string | null
}

interface NameFieldProps {
  requestName: string
  setRequestName: (value: string) => void
}

function NameField({ requestName, setRequestName }: NameFieldProps): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {t('collections.requestName')}
      </label>
      <input
        type="text"
        value={requestName}
        onChange={(event) => {
          setRequestName(event.target.value)
        }}
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
        placeholder={t('collections.requestNamePlaceholder')}
        required
      />
    </div>
  )
}

interface NotesFieldProps {
  notes: string
  setNotes: (value: string) => void
}

function NotesField({ notes, setNotes }: NotesFieldProps): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {t('collections.notes')}
      </label>
      <textarea
        value={notes}
        onChange={(event) => {
          setNotes(event.target.value)
        }}
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
        rows={TEXTAREA_ROWS}
        placeholder={t('collections.notesPlaceholder')}
      />
    </div>
  )
}

interface TemplatePickerProps {
  caseId: string
  selectedTemplateId: string | null
  setSelectedTemplateId: (value: string | null) => void
}

function TemplatePicker(props: TemplatePickerProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const { caseId, selectedTemplateId, setSelectedTemplateId } = props
  const { templates, loading, refetch } = useArtefactTemplates(caseId)
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {t('velociraptor.templateStep.label', 'Collector Template')}
      </label>
      <div className="max-h-60 overflow-y-auto rounded-lg border border-slate-200 p-3 dark:border-slate-700">
        <VelociraptorTemplateStep
          templates={templates}
          selectedTemplateId={selectedTemplateId}
          onSelect={setSelectedTemplateId}
          loading={loading}
          caseId={caseId}
          userRole="case_user"
          onTemplatesChanged={() => {
            void refetch()
          }}
        />
      </div>
    </div>
  )
}

interface ModalFooterProps {
  onClose: () => void
  updating: boolean
  disabled: boolean
}

function ModalFooter({ onClose, updating, disabled }: ModalFooterProps): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex justify-end gap-3 pt-4">
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        {t('actions.cancel')}
      </button>
      <button
        type="submit"
        disabled={disabled}
        className="inline-flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-sm font-medium text-white hover:bg-[#5c6e94] disabled:opacity-50"
      >
        {updating && <RefreshCw className="h-4 w-4 animate-spin" />}
        {t('collections.saveChanges')}
      </button>
    </div>
  )
}

function ErrorBox({ error }: { error: string }): React.ReactElement {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/50">
      <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
    </div>
  )
}

function TargetEndpointsNote(): React.ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="rounded-lg bg-slate-50 p-3 text-sm dark:bg-slate-800/50">
      <p className="text-slate-500 dark:text-slate-400">{t('collections.editTargetNote')}</p>
      <p className="mt-1 text-slate-600 dark:text-slate-300">
        {t('collections.endpointCount', { count: 0 })}
      </p>
    </div>
  )
}

export function EditCollectionModal(props: EditCollectionModalProps): React.ReactElement {
  const { t } = useTranslation('cases')
  const { caseId, request, onClose, onUpdate, updating, error } = props
  const [requestName, setRequestName] = useState(request.requestName)
  const [notes, setNotes] = useState(request.notes ?? '')
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    request.artefactTemplateId ?? null
  )

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault()
    if (!requestName) return
    const input: UpdateCollectionRequestInput = { requestName }
    if (notes) input.notes = notes
    if (selectedTemplateId) input.artefactTemplateId = selectedTemplateId
    onUpdate(input)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl dark:bg-slate-900">
        <h3 className="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
          {t('collections.editCollectionRequest')}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <ErrorBox error={error} />}
          <NameField requestName={requestName} setRequestName={setRequestName} />
          {request.collectorType === 'velociraptor' && (
            <TemplatePicker
              caseId={caseId}
              selectedTemplateId={selectedTemplateId}
              setSelectedTemplateId={setSelectedTemplateId}
            />
          )}
          <TargetEndpointsNote />
          <NotesField notes={notes} setNotes={setNotes} />
          <ModalFooter onClose={onClose} updating={updating} disabled={updating || !requestName} />
        </form>
      </div>
    </div>
  )
}
